import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Users,
  FileSearch,
  ScanLine,
  Calendar,
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { Spinner } from '../../design-system';

// Types
interface ScanCostSummary {
  scan_id: string;
  scan_type: string | null;
  repository_name: string | null;
  total_cost_usd: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  request_count: number;
  created_at: string | null;
  user_id: string | null;
}

interface UserCostSummary {
  user_id: string;
  user_email: string | null;
  total_cost_usd: number;
  total_tokens: number;
  scan_count: number;
  request_count: number;
}

interface AssessmentCostSummary {
  finding_id: string;
  finding_check_id: string | null;
  finding_file_path: string | null;
  total_cost_usd: number;
  total_tokens: number;
  request_count: number;
  created_at: string | null;
}

interface CostsResponse<T> {
  items: T[];
  total: number;
  total_cost_usd: number;
}

type TabType = 'scans' | 'users' | 'assessments';
type DateRange = '7d' | '30d' | '90d' | 'all';

const CostTrackingSection: React.FC = () => {
  const authenticatedApi = useAuthenticatedApi();

  // State
  const [activeTab, setActiveTab] = useState<TabType>('scans');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Scan costs state
  const [scanCosts, setScanCosts] = useState<CostsResponse<ScanCostSummary> | null>(null);
  const [scanPage, setScanPage] = useState(0);

  // User costs state
  const [userCosts, setUserCosts] = useState<CostsResponse<UserCostSummary> | null>(null);
  const [userPage, setUserPage] = useState(0);

  // Assessment costs state
  const [assessmentCosts, setAssessmentCosts] = useState<CostsResponse<AssessmentCostSummary> | null>(null);
  const [assessmentPage, setAssessmentPage] = useState(0);

  const pageSize = 25;

  // Calculate date range
  const getDateRange = useCallback((): { start_date?: string; end_date?: string } => {
    if (dateRange === 'all') return {};

    const end = new Date();
    const start = new Date();

    switch (dateRange) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
    }

    return {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    };
  }, [dateRange]);

  // Fetch scan costs
  const fetchScanCosts = useCallback(async () => {
    if (!authenticatedApi) return;

    setLoading(true);
    setError(null);

    try {
      const { start_date, end_date } = getDateRange();
      const params = new URLSearchParams({
        skip: (scanPage * pageSize).toString(),
        limit: pageSize.toString(),
      });
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const response = await authenticatedApi.get(
        `${apiEndpoints.admin.usageCostsByScan}?${params}`
      );
      setScanCosts(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch scan costs');
    } finally {
      setLoading(false);
    }
  }, [authenticatedApi, scanPage, getDateRange]);

  // Fetch user costs
  const fetchUserCosts = useCallback(async () => {
    if (!authenticatedApi) return;

    setLoading(true);
    setError(null);

    try {
      const { start_date, end_date } = getDateRange();
      const params = new URLSearchParams({
        skip: (userPage * pageSize).toString(),
        limit: pageSize.toString(),
      });
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const response = await authenticatedApi.get(
        `${apiEndpoints.admin.usageCostsByUser}?${params}`
      );
      setUserCosts(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch user costs');
    } finally {
      setLoading(false);
    }
  }, [authenticatedApi, userPage, getDateRange]);

  // Fetch assessment costs
  const fetchAssessmentCosts = useCallback(async () => {
    if (!authenticatedApi) return;

    setLoading(true);
    setError(null);

    try {
      const { start_date, end_date } = getDateRange();
      const params = new URLSearchParams({
        skip: (assessmentPage * pageSize).toString(),
        limit: pageSize.toString(),
      });
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const response = await authenticatedApi.get(
        `${apiEndpoints.admin.usageCostsByAssessment}?${params}`
      );
      setAssessmentCosts(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch assessment costs');
    } finally {
      setLoading(false);
    }
  }, [authenticatedApi, assessmentPage, getDateRange]);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'scans') {
      fetchScanCosts();
    } else if (activeTab === 'users') {
      fetchUserCosts();
    } else if (activeTab === 'assessments') {
      fetchAssessmentCosts();
    }
  }, [activeTab, fetchScanCosts, fetchUserCosts, fetchAssessmentCosts]);

  // Reset page when date range changes
  useEffect(() => {
    setScanPage(0);
    setUserPage(0);
    setAssessmentPage(0);
  }, [dateRange]);

  // Format currency
  const formatCurrency = (value: number): string => {
    return `$${value.toFixed(4)}`;
  };

  // Format number with commas
  const formatNumber = (value: number): string => {
    return value.toLocaleString();
  };

  // Format date
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Truncate ID
  const truncateId = (id: string, maxLength = 12): string => {
    if (id.length <= maxLength) return id;
    return `${id.slice(0, maxLength)}...`;
  };

  // Get current data and pagination
  const getCurrentData = () => {
    switch (activeTab) {
      case 'scans':
        return { data: scanCosts, page: scanPage, setPage: setScanPage };
      case 'users':
        return { data: userCosts, page: userPage, setPage: setUserPage };
      case 'assessments':
        return { data: assessmentCosts, page: assessmentPage, setPage: setAssessmentPage };
    }
  };

  const { data: currentData, page: currentPage, setPage: setCurrentPage } = getCurrentData();
  const totalPages = currentData ? Math.ceil(currentData.total / pageSize) : 0;

  // Tab button component
  const TabButton: React.FC<{ tab: TabType; label: string; icon: React.ReactNode }> = ({
    tab,
    label,
    icon,
  }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        activeTab === tab
          ? 'bg-brand-primary text-black'
          : 'bg-ui-background-tertiary text-ui-text-secondary hover:bg-ui-background-tertiary'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="space-y-6">
        {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-ui-text-primary">
            Cost Tracking
          </h2>
          <p className="text-sm text-ui-text-tertiary mt-1">
            View LLM usage costs aggregated by scan, user, or assessment
          </p>
        </div>

        {/* Date Range Filter */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-ui-text-tertiary" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            className="px-3 py-1.5 text-sm bg-ui-background-secondary border border-ui-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Summary Card */}
      {currentData && (
        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-6 h-6 text-brand-primary" />
            <span className="text-sm font-medium text-ui-text-secondary">Total Cost</span>
          </div>
          <div className="text-3xl font-bold text-ui-text-primary">{formatCurrency(currentData.total_cost_usd)}</div>
          <div className="text-sm text-ui-text-tertiary mt-1">
            {formatNumber(currentData.total)} {activeTab === 'scans' ? 'scans' : activeTab === 'users' ? 'users' : 'assessments'}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <TabButton tab="scans" label="By Scan" icon={<ScanLine className="w-4 h-4" />} />
        <TabButton tab="users" label="By User" icon={<Users className="w-4 h-4" />} />
        <TabButton tab="assessments" label="By Assessment" icon={<FileSearch className="w-4 h-4" />} />
        <button
          onClick={() => {
            if (activeTab === 'scans') fetchScanCosts();
            else if (activeTab === 'users') fetchUserCosts();
            else fetchAssessmentCosts();
          }}
          className="ml-auto flex items-center gap-2 px-3 py-2 text-sm text-ui-text-secondary hover:text-ui-text-primary transition-colors"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size={32} />
        </div>
      )}

      {/* Tables */}
      {!loading && currentData && (
        <div className="overflow-x-auto">
          {/* Scans Table */}
          {activeTab === 'scans' && scanCosts && (
            <table className="w-full">
              <thead className="bg-ui-background-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Scan ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Repository
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Requests
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ui-background-secondary divide-y divide-ui-border-primary">
                {scanCosts.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-ui-text-tertiary">
                      No scan costs found for this period
                    </td>
                  </tr>
                ) : (
                  scanCosts.items.map((scan) => (
                    <tr key={scan.scan_id} className="hover:bg-ui-background-tertiary">
                      <td className="px-4 py-3 text-sm font-mono text-ui-text-primary">
                        {truncateId(scan.scan_id)}
                      </td>
                      <td className="px-4 py-3 text-sm text-ui-text-secondary">
                        {scan.scan_type || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-ui-text-secondary">
                        {scan.repository_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-ui-text-primary">
                        {formatCurrency(scan.total_cost_usd)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {formatNumber(scan.total_tokens)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {scan.request_count}
                      </td>
                      <td className="px-4 py-3 text-sm text-ui-text-secondary">
                        {formatDate(scan.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* Users Table */}
          {activeTab === 'users' && userCosts && (
            <table className="w-full">
              <thead className="bg-ui-background-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Scans
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Requests
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ui-background-secondary divide-y divide-ui-border-primary">
                {userCosts.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-ui-text-tertiary">
                      No user costs found for this period
                    </td>
                  </tr>
                ) : (
                  userCosts.items.map((user) => (
                    <tr key={user.user_id} className="hover:bg-ui-background-tertiary">
                      <td className="px-4 py-3 text-sm text-ui-text-primary">
                        {user.user_email || truncateId(user.user_id)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-ui-text-primary">
                        {formatCurrency(user.total_cost_usd)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {formatNumber(user.total_tokens)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {user.scan_count}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {user.request_count}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* Assessments Table */}
          {activeTab === 'assessments' && assessmentCosts && (
            <table className="w-full">
              <thead className="bg-ui-background-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Finding ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Check ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    File Path
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">
                    Requests
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ui-background-secondary divide-y divide-ui-border-primary">
                {assessmentCosts.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-ui-text-tertiary">
                      No assessment costs found for this period
                    </td>
                  </tr>
                ) : (
                  assessmentCosts.items.map((assessment) => (
                    <tr key={assessment.finding_id} className="hover:bg-ui-background-tertiary">
                      <td className="px-4 py-3 text-sm font-mono text-ui-text-primary">
                        {truncateId(assessment.finding_id)}
                      </td>
                      <td className="px-4 py-3 text-sm text-ui-text-secondary">
                        {assessment.finding_check_id || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-ui-text-secondary max-w-xs truncate">
                        {assessment.finding_file_path || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-ui-text-primary">
                        {formatCurrency(assessment.total_cost_usd)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {formatNumber(assessment.total_tokens)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-ui-text-secondary">
                        {assessment.request_count}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pagination */}
      {currentData && currentData.total > pageSize && (
        <div className="flex items-center justify-between px-4 py-3 bg-ui-background-secondary border-t border-ui-border-primary rounded-b-lg">
          <div className="text-sm text-ui-text-tertiary">
            Showing {currentPage * pageSize + 1} to{' '}
            {Math.min((currentPage + 1) * pageSize, currentData.total)} of{' '}
            {formatNumber(currentData.total)} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="p-2 rounded-lg border border-ui-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ui-background-tertiary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-ui-text-secondary">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage >= totalPages - 1}
              className="p-2 rounded-lg border border-ui-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ui-background-tertiary transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CostTrackingSection;
